import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Grid } from '@mui/material';
import { theme } from 'theme';
import { setIsEligible, setPartnerId } from 'reducers/partnerStatusReducer';
import { OrderNewProductOrService } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import { PcoTabNames, setShownPage } from 'reducers/shownPageReducer';
import { setIsRequiredNewBr, setMandatePartnerRequestId } from 'reducers/mandateReducer';
import { setSelectedProduct } from 'reducers/tableReducer';
import { ProductsAndServices } from 'views/ProductsAndServices/ProductsAndServices';
import { DiscretionaryMandate } from 'views/DiscretionaryMandate/DiscretionaryMandate';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { CaseOverview } from 'views/Case/CaseOverview';
import { selectSelectedProduct } from 'selectors/tableSelector';
import { selectShownPage } from 'selectors/shownPageSelector';
import { VideoIdentificationFormContainer } from 'views/VideoIdentification/VideoIdentificationFormContainer';
import { VideoIdentificationDetails } from 'views/VideoIdentification/VideoIdentificationDetails';
import { SubscriptionManagementContainer } from 'views/SubscriptionManagement/SubscriptionManagementContainer';
import { AddProductOrService } from 'views/AddProductsOrServices/AddProductsOrServices';
import ProductConfiguration from 'views/ProductConfiguration/ProductConfiguration';
import ProductDetails from 'views/ProductDetails/ProductDetails';
import { setAccountNumber } from 'reducers/accountReducer';
import { getPartnerThunk } from 'thunks/partnerThunk';
import { getIsPartnerEligible } from 'services/partner/getIsPartnerEligible';
import { AppDispatch } from 'store';
import { fetchClippPricingUrlData } from 'reducers/clippPricingReducer';
import { fetchAllServiceAgreementsForAccount } from 'thunks/accountServiceAgreementsThunk';
import { jbColors } from 'theme/jbColors';
import { MemberCodeOverview } from 'views/MemberZoneAccess/MemberZoneStatus/MemberCodeOverview';
import { CreateAccessCode } from 'views/MemberZoneAccess/CreateAccessCode/CreateAccesCode';
import { fetchDocumentsToDistribute } from 'thunks/productDocumentsThunk';
import { fetchEamEfaDashboardUrlsData } from '../../reducers/eamEfaDashboardReducer';
import { closeModal, openModal } from '../../reducers/modalReducer';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.palette.common.white,
  },
  innerContainer: {
    paddingTop: theme.spacing(2),
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.common.white,
  },
  innerContainerRight: {
    paddingTop: theme.spacing(2),
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.common.white,
  },
  overviewProductsRightSideContainer: {
    paddingTop: theme.spacing(2),
    backgroundColor: jbColors.core.stone[30],
  },
};

type Props = {
  shownPage: string;
};

export const Overview = ({ shownPage }: Props) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { partnerId } = useParams();
  const { productId } = useParams<{
    partnerId: string;
    productId: string;
  }>();

  const searchParams = new URLSearchParams(location.search);
  const partnerNumberFromSearchParam = searchParams.get('partnerNumber');
  const partnerIdFromStore = useSelector(selectPartnerId);
  const selectedProductId: string | undefined = useSelector(selectSelectedProduct);
  const shownPageFromStore = useSelector(selectShownPage);

  useEffect(() => {
    const checkEligibility = async (id: string) => {
      try {
        const eligibility = await getIsPartnerEligible(id);

        dispatch(setIsEligible(eligibility));
      } catch (axiosError: unknown) {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: axiosError,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when checking partner for eligibility',
                userErrorMessage: `An error occurred when checking partner for eligibility. Please try again later.`,
              },
            },
          }),
        );
      }
    };

    if (partnerId && !partnerNumberFromSearchParam && !partnerIdFromStore) {
      checkEligibility(partnerId);
      return;
    }
    if (partnerNumberFromSearchParam && !partnerIdFromStore && !partnerId) {
      checkEligibility(partnerNumberFromSearchParam);
      return;
    }
    if (partnerIdFromStore && !partnerId && !partnerNumberFromSearchParam) {
      checkEligibility(partnerIdFromStore);
      return;
    }
  }, [partnerId, dispatch, partnerNumberFromSearchParam, partnerIdFromStore]);

  useEffect(() => {
    if (partnerNumberFromSearchParam) {
      dispatch(setPartnerId(partnerNumberFromSearchParam));
    }
  }, [partnerNumberFromSearchParam, dispatch]);

  useEffect(() => {
    if (partnerId) {
      dispatch(fetchAllServiceAgreementsForAccount(partnerId, undefined, undefined, true));
    } else if (partnerNumberFromSearchParam) {
      dispatch(fetchAllServiceAgreementsForAccount(partnerNumberFromSearchParam, undefined, undefined, true));
    }
  }, [dispatch, partnerNumberFromSearchParam, partnerId]);

  useEffect(() => {
    if (partnerId && partnerId !== '') {
      dispatch(setPartnerId(partnerId));
      dispatch(getPartnerThunk(partnerId));
      dispatch(fetchClippPricingUrlData(partnerId));
      dispatch(fetchEamEfaDashboardUrlsData(partnerId));
      dispatch(setMandatePartnerRequestId(partnerId));
      dispatch(setIsRequiredNewBr(true));
    }

    if (partnerNumberFromSearchParam && partnerNumberFromSearchParam !== '') {
      dispatch(setPartnerId(partnerNumberFromSearchParam));
      dispatch(getPartnerThunk(partnerNumberFromSearchParam));
      dispatch(fetchClippPricingUrlData(partnerNumberFromSearchParam));
      dispatch(fetchEamEfaDashboardUrlsData(partnerNumberFromSearchParam));
      dispatch(setMandatePartnerRequestId(partnerNumberFromSearchParam));
      dispatch(setIsRequiredNewBr(true));
    }

    if (partnerId) {
      dispatch(fetchDocumentsToDistribute(null, partnerId));
    }
    if (partnerNumberFromSearchParam) {
      dispatch(fetchDocumentsToDistribute(null, partnerNumberFromSearchParam));
    }
  }, [dispatch, partnerId, partnerNumberFromSearchParam]);

  useEffect(() => {
    dispatch(setShownPage(shownPage));
  }, [dispatch, shownPage]);

  useEffect(() => {
    if (productId) {
      dispatch(setSelectedProduct(productId));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    dispatch(setAccountNumber(undefined));
  }, [dispatch]);

  const renderSwitch = (param: string | undefined) => {
    switch (param) {
      case PcoTabNames.NewMandate:
        return <DiscretionaryMandate title={'NEW'} isPartner={true} id={partnerId} />;
      case PcoTabNames.OverviewMemberCode:
        return <MemberCodeOverview />;
      case PcoTabNames.CreateMemberCode:
        return <CreateAccessCode />;
      case PcoTabNames.Case:
        return <CaseOverview productId={selectedProductId} />;
      case PcoTabNames.VideoIdentificationForm:
        return <VideoIdentificationFormContainer />;
      case PcoTabNames.VideoIdentification:
        return <VideoIdentificationDetails productId={selectedProductId} />;
      case PcoTabNames.SubscriptionManagement:
        return <SubscriptionManagementContainer />;
      case PcoTabNames.ProductConfiguration:
        return <ProductConfiguration />;
      case PcoTabNames.ProductDetails:
        return <ProductDetails />;
      case PcoTabNames.AddProductOrService:
        return <AddProductOrService brUid={null} partnerId={partnerIdFromStore} accountNumber={null} />;
      case PcoTabNames.OrderNewProductOrService:
        return <OrderNewProductOrService brUid={null} partnerNumber={partnerIdFromStore} />;
      default:
        return <OrderNewProductOrService brUid={null} partnerNumber={partnerIdFromStore} />;
    }
  };

  return (
    <Grid data-testid="overview" component="main" container sx={styles.container}>
      <Grid item xs={6} sx={styles.innerContainer}>
        <ProductsAndServices brUid={null} partnerId={partnerId ? partnerId : partnerNumberFromSearchParam} />
      </Grid>
      <Grid item xs={6} sx={styles.overviewProductsRightSideContainer}>
        {renderSwitch(shownPageFromStore)}
      </Grid>
    </Grid>
  );
};
